import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail, downloadReport } from "@/lib/api";
import { inr, fmtDate, today, billNo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import PaymentMethods from "@/components/PaymentMethods";
import { DatePicker } from "@/components/DatePicker";
import { SearchSelect } from "@/components/SearchSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, FileDown, FileSpreadsheet, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

export default function PaymentEntry() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [supplierId, setSupplierId] = useState("");
  const [date, setDate] = useState(today());
  const [alloc, setAlloc] = useState({}); // purchase_id -> {checked, amount}
  const [adjustment, setAdjustment] = useState("");
  const [adjType, setAdjType] = useState("deduct");
  const [methods, setMethods] = useState([{ mode: "cash", amount: "" }]);
  const [methodsAuto, setMethodsAuto] = useState(true);
  const [memo, setMemo] = useState("");
  const [roundAmount, setRoundAmount] = useState("");

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers-min"], queryFn: async () => (await api.get("/suppliers")).data });

  const { data: bills = [] } = useQuery({
    queryKey: ["outstanding", supplierId],
    queryFn: async () => (await api.get("/purchases/outstanding", { params: { supplier_id: supplierId } })).data,
    enabled: !!supplierId,
  });

  const { data: payments = [] } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });

  const toggle = (b) => setAlloc((a) => {
    if (a[b.id]) { const { [b.id]: _, ...rest } = a; return rest; }
    return { ...a, [b.id]: true };
  });

  const selectedBills = bills.filter((b) => alloc[b.id]);
  const selectedTotal = +selectedBills.reduce((s, b) => s + Number(b.balance || 0), 0).toFixed(2);

  useEffect(() => {
    if (methodsAuto) setMethods((ms) => ms.map((m, i) => (i === 0 ? { ...m, amount: selectedTotal || "" } : m)));
  }, [selectedTotal, methodsAuto]);

  const handleSetMethods = (ms) => { setMethodsAuto(false); setMethods(ms); };

  const methodsTotal = methods.reduce((s, m) => s + Number(m.amount || 0), 0);
  const signedAdj = adjType === "add" ? Number(adjustment || 0) : -Number(adjustment || 0);
  const settleTarget = +(methodsTotal - signedAdj).toFixed(2);

  const preview = (() => {
    const out = {};
    let remaining = settleTarget;
    selectedBills.forEach((b) => {
      const give = Math.max(0, Math.min(remaining, Number(b.balance)));
      if (give > 0) { out[b.id] = +give.toFixed(2); remaining = +(remaining - give).toFixed(2); }
    });
    return out;
  })();
  const allocTotal = Object.values(preview).reduce((s, v) => s + v, 0);
  const netAmount = +(allocTotal + signedAdj).toFixed(2);
  const leftover = +(settleTarget - allocTotal).toFixed(2);
  const allocationList = Object.entries(preview).map(([pid, amt]) => ({ purchase_id: pid, amount: amt }));

  const reset = () => { setAlloc({}); setAdjustment(""); setAdjType("deduct"); setMethods([{ mode: "cash", amount: "" }]); setMethodsAuto(true); setMemo(""); setRoundAmount(""); };

  const save = useMutation({
    mutationFn: async () => api.post("/payments", {
      supplier_id: supplierId, date,
      allocations: allocationList,
      adjustment: Number(adjustment || 0), adjustment_type: adjType,
      methods: methods.filter((m) => Number(m.amount || 0) > 0).map((m) => ({ ...m, amount: Number(m.amount || 0) })),
      memo,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outstanding"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["purchases"] });
      toast.success("Payment recorded");
      reset();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/payments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["outstanding"] }); toast.success("Payment reversed"); },
  });

  // --- Edit existing payment ---
  const [editPay, setEditPay] = useState(null);
  const [eAlloc, setEAlloc] = useState([]); // [{purchase_id, purchase_no, amount}]
  const [eAdjustment, setEAdjustment] = useState("");
  const [eAdjType, setEAdjType] = useState("deduct");
  const [eMethods, setEMethods] = useState([]);
  const [eMemo, setEMemo] = useState("");
  const [eDate, setEDate] = useState(today());

  const openEdit = (p) => {
    setEditPay(p);
    setEAlloc((p.allocations || []).map((a) => ({ purchase_id: a.purchase_id, purchase_no: a.purchase_no, bill_number: a.bill_number, amount: a.amount })));
    setEAdjustment(p.adjustment || "");
    setEAdjType(p.adjustment_type || "deduct");
    setEMethods((p.methods || []).map((m) => ({ ...m })));
    setEMemo(p.memo || "");
    setEDate(p.date || today());
  };

  const eAllocTotal = eAlloc.reduce((s, a) => s + Number(a.amount || 0), 0);
  const eNet = +(eAllocTotal + (eAdjType === "add" ? Number(eAdjustment || 0) : -Number(eAdjustment || 0))).toFixed(2);

  const edit = useMutation({
    mutationFn: async () => api.put(`/payments/${editPay.id}`, {
      supplier_id: editPay.supplier_id, date: eDate,
      allocations: eAlloc.map((a) => ({ purchase_id: a.purchase_id, amount: Number(a.amount || 0) })),
      adjustment: Number(eAdjustment || 0), adjustment_type: eAdjType,
      methods: eMethods.filter((m) => Number(m.amount || 0) > 0).map((m) => ({ ...m, amount: Number(m.amount || 0) })),
      memo: eMemo,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["outstanding"] });
      qc.invalidateQueries({ queryKey: ["purchases"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Payment updated");
      setEditPay(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const mismatch = false;
  const [paySearch, setPaySearch] = useState("");
  const shownPayments = useMemo(() => {
    const s = paySearch.trim().toLowerCase();
    if (!s) return payments;
    return payments.filter((p) =>
      (p.payment_no || "").toLowerCase().includes(s) ||
      (p.supplier_name || "").toLowerCase().includes(s) ||
      (p.allocations || []).some((a) => (a.bill_number || "").toLowerCase().includes(s)));
  }, [payments, paySearch]);

  return (
    <div>
      <PageTitle title="Payment Entry" subtitle="Settle supplier bills with single or split payments" />

      {isAdmin && (
      <Card className="p-5 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <Label>Supplier *</Label>
            <div className="mt-1.5">
              <SearchSelect
                value={supplierId}
                onChange={(v) => { setSupplierId(v); reset(); }}
                options={suppliers.map((s) => ({ value: s.id, label: `${s.supplier_code} — ${s.name}` }))}
                placeholder="Select supplier"
                searchPlaceholder="Type supplier name or code…"
                testid="payment-supplier"
              />
            </div>
          </div>
          <div><Label>Payment Date</Label><div className="mt-1.5"><DatePicker value={date} onChange={setDate} testid="payment-date" /></div></div>
        </div>

        {supplierId && (
          <>
            <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Outstanding Bills</h3>
            <div className="overflow-x-auto ssacc-scroll rounded-lg border">
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead className="w-10"></TableHead><TableHead>Bill No</TableHead><TableHead>Date</TableHead>
                  <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Balance</TableHead><TableHead className="text-right w-40">Applied</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bills.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No outstanding bills for this supplier.</TableCell></TableRow>
                  ) : bills.map((b) => {
                    const applied = preview[b.id] || 0;
                    const checkedBill = !!alloc[b.id];
                    return (
                      <TableRow key={b.id} data-testid={`bill-row-${b.purchase_no}`} className={checkedBill ? "bg-primary/5" : ""}>
                        <TableCell><Checkbox checked={checkedBill} onCheckedChange={() => toggle(b)} data-testid={`bill-check-${b.purchase_no}`} /></TableCell>
                        <TableCell className="font-semibold text-primary">{billNo(b.bill_number)}</TableCell>
                        <TableCell>{fmtDate(b.date)}</TableCell>
                        <TableCell className="text-right data-num">{inr(b.total)}</TableCell>
                        <TableCell className="text-right data-num font-medium">{inr(b.balance)}</TableCell>
                        <TableCell className="text-right data-num" data-testid={`bill-applied-${b.purchase_no}`}>
                          {checkedBill ? (
                            <span>
                              <span className="font-semibold text-success">{inr(applied)}</span>
                              {applied < Number(b.balance) && <span className="block text-xs text-destructive">bal {inr(Number(b.balance) - applied)}</span>}
                            </span>
                          ) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <div className="rounded-xl bg-muted/40 p-5 space-y-3">
                <p className="text-xs text-muted-foreground">The ticked bills total is auto-filled under <b>Payment Modes</b> and applied oldest-first — edit it to pay less (partial payment) or split across modes. The balance stays on the last bill.</p>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount Paid (modes)</span><span className="font-bold data-num" data-testid="methods-total">{inr(methodsTotal)}</span></div>
                <div className="flex items-center gap-3">
                  <div className="flex-1"><Label className="text-xs">Adjustment / Discount (₹)</Label><Input type="number" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="mt-1 bg-card" data-testid="adjustment-value" /></div>
                  <div className="w-32">
                    <Label className="text-xs">Type</Label>
                    <Select value={adjType} onValueChange={setAdjType}>
                      <SelectTrigger className="mt-1 bg-card" data-testid="adjustment-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="deduct">Deduct (−)</SelectItem><SelectItem value="add">Add (+)</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between border-t pt-2"><span className="font-bold">Settled Against Bills</span><span className="font-display text-xl font-bold text-primary data-num" data-testid="alloc-total">{inr(allocTotal)}</span></div>
                {leftover > 0.01 && <p className="text-xs text-accent-foreground bg-accent/15 rounded px-2 py-1" data-testid="leftover-note">₹{leftover.toFixed(2)} more than the ticked bills balance — tick more bills or reduce the amount.</p>}
                <div><Label className="text-xs">Memo (adjustment / bill details)</Label><Textarea value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1 bg-card" data-testid="payment-memo" /></div>
              </div>

              <div className="rounded-xl border p-5">
                <Label className="text-xs">Payment Modes (multiple cheques allowed)</Label>
                <div className="mt-2"><PaymentMethods methods={methods} setMethods={handleSetMethods} modes={["cash", "upi", "neft", "cheque", "card"]} testidPrefix="spm" /></div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={reset}>Clear</Button>
              <Button onClick={() => save.mutate()} disabled={selectedBills.length === 0 || allocTotal <= 0 || save.isPending} data-testid="save-payment">
                {save.isPending ? "Saving…" : "Record Payment"}
              </Button>
            </div>
          </>
        )}
      </Card>
      )}

      {isAdmin && supplierId && selectedBills.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 w-64 rounded-xl border bg-card shadow-2xl p-4 space-y-1.5" data-testid="payment-float-total">
          <div className="text-xs font-semibold text-muted-foreground">{selectedBills.length} bill{selectedBills.length > 1 ? "s" : ""} selected</div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Bills Total</span><span className="data-num font-semibold" data-testid="float-selected-total">{inr(selectedTotal)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Settled</span><span className="data-num font-semibold" data-testid="float-settled">{inr(allocTotal)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{adjType === "add" ? "+ " : "− "}Adjustment</span><span className="data-num">{inr(Number(adjustment || 0))}</span></div>
          <div className="flex justify-between border-t pt-1.5"><span className="font-bold">Net</span><span className="font-display text-lg font-bold text-primary data-num" data-testid="float-net">{inr(netAmount)}</span></div>
        </div>
      )}

      <PageTitle title="Recent Payments" subtitle={`${payments.length} entries`}>
        <Button variant="outline" onClick={() => downloadReport("/reports/payments/export", { fmt: "pdf" }, "payments")}><FileDown className="h-4 w-4" /> PDF</Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/payments/export", { fmt: "excel" }, "payments")}><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
      </PageTitle>
      <Card className="overflow-hidden">
        <div className="p-3 border-b">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by bill number…" value={paySearch} onChange={(e) => setPaySearch(e.target.value)} className="pl-9" data-testid="payment-search" />
          </div>
        </div>
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Payment No</TableHead><TableHead>Date</TableHead><TableHead>Supplier</TableHead>
              <TableHead>Bills</TableHead><TableHead>Modes</TableHead><TableHead className="text-right">Net Paid</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {shownPayments.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">{paySearch ? "No payments match your search." : "No payments yet."}</TableCell></TableRow>
              ) : shownPayments.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.payment_no}`}>
                  <TableCell className="font-semibold text-primary">{p.payment_no}</TableCell>
                  <TableCell>{fmtDate(p.date)}</TableCell>
                  <TableCell>{p.supplier_name}</TableCell>
                  <TableCell className="text-xs">{p.allocations?.map((a) => billNo(a.bill_number)).join(", ")}</TableCell>
                  <TableCell className="text-xs">{p.methods?.map((m) => `${m.mode.toUpperCase()} ${inr(m.amount)}`).join(", ")}</TableCell>
                  <TableCell className="text-right font-semibold data-num">{inr(p.net_amount)}</TableCell>
                  <TableCell className="text-right">
                    {isAdmin && <><Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-payment-${p.payment_no}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del.mutate(p.id)} data-testid={`del-payment-${p.payment_no}`}><Trash2 className="h-4 w-4" /></Button></>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editPay} onOpenChange={(o) => !o && setEditPay(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto ssacc-scroll">
          <DialogHeader>
            <DialogTitle>Edit {editPay?.payment_no}</DialogTitle>
            <DialogDescription>{editPay?.supplier_name} — correct allocation amounts, modes, adjustment or date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Payment Date</Label><div className="mt-1.5"><DatePicker value={eDate} onChange={setEDate} testid="epay-date" /></div></div>

            <div>
              <Label className="text-xs">Bills in this payment</Label>
              <div className="mt-1.5 space-y-2">
                {eAlloc.map((a, i) => (
                  <div key={a.purchase_id} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-primary w-28">{billNo(a.bill_number)}</span>
                    <Input type="number" value={a.amount} onChange={(e) => setEAlloc(eAlloc.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} className="h-9" data-testid={`epay-alloc-${a.purchase_no}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1"><Label className="text-xs">Adjustment (₹)</Label><Input type="number" value={eAdjustment} onChange={(e) => setEAdjustment(e.target.value)} className="mt-1" data-testid="epay-adjustment" /></div>
              <div className="w-32">
                <Label className="text-xs">Type</Label>
                <Select value={eAdjType} onValueChange={setEAdjType}>
                  <SelectTrigger className="mt-1" data-testid="epay-adjtype"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="deduct">Deduct (−)</SelectItem><SelectItem value="add">Add (+)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded-lg bg-muted/40 px-3 py-2 flex justify-between text-sm">
              <span className="text-muted-foreground">Net Payable</span>
              <span className="font-bold data-num" data-testid="epay-net">{inr(eNet)}</span>
            </div>

            <div>
              <Label className="text-xs">Payment Modes</Label>
              <div className="mt-1.5"><PaymentMethods methods={eMethods} setMethods={setEMethods} modes={["cash", "upi", "neft", "cheque", "card"]} testidPrefix="epm" /></div>
            </div>

            <div><Label className="text-xs">Memo</Label><Textarea value={eMemo} onChange={(e) => setEMemo(e.target.value)} className="mt-1.5" data-testid="epay-memo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPay(null)}>Cancel</Button>
            <Button onClick={() => edit.mutate()} disabled={edit.isPending} data-testid="save-payment-edit">{edit.isPending ? "Saving…" : "Update Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
